import { useCallback, useEffect, useState } from "react";
import type { Locale } from "../../shared/dto";
import { PRODUCT_ARCHETYPE_LABELS, productArchetypeOf } from "../../shared/stripe-product-metadata";
import { translateCount } from "../i18n";
import "./purchase-licenses.css";

/**
 * F155: the licence the client BOUGHT, read live from the BuddyPro platform.
 *
 * 🔴 Not the same fact as the licence key in Nastavení. That one
 * (`client_resources.license_key`, F105) is what the Instance Creator pushed
 * here after it built THIS client's instance, and the client receives it on
 * handover step 1. This card is the platform's own record of the purchase, so
 * it answers a question no row in our database can: did the payment really
 * create a licence, how many credits does it carry, and is it already sitting
 * on a bot. The copy names both sides so a partner reading the two cards on one
 * page cannot take them for one number said twice.
 */

export interface PurchaseLicenseRow {
  license: string;
  credits: number | null;
  productType: string | null;
  botUsername: string | null;
  disabled: boolean;
}

type CardState = "loading" | "unconfigured" | "error" | "none" | "ready";

export function PurchaseLicenseCard(props: { clientId: string; t: (key: string) => string; locale: Locale }) {
  const [state, setState] = useState<CardState>("loading");
  const [licenses, setLicenses] = useState<PurchaseLicenseRow[]>([]);

  const load = useCallback(async () => {
    setState("loading");
    setLicenses([]);
    try {
      const response = await fetch(`/api/staff/clients/${props.clientId}/purchase-licenses`, {
        credentials: "include"
      });
      const body = await response.json() as {
        ok: boolean;
        error?: string;
        failed?: boolean;
        licenses?: PurchaseLicenseRow[];
      };
      // 🔴 The unconfigured refusal is read by its NAME, never by the 503: a
      // Worker that is simply down answers 5xx too, and the two sentences are
      // opposite (one says nothing is switched on, the other says try again).
      if (response.status === 503 && body.error === "license_lookup_unconfigured") {
        setState("unconfigured");
        return;
      }
      if (!response.ok || !body.ok) throw new Error("license_lookup_failed");
      if (body.failed) { setState("error"); return; }
      const rows = body.licenses ?? [];
      if (rows.length === 0) { setState("none"); return; }
      setLicenses(rows);
      setState("ready");
    } catch {
      setState("error");
    }
  }, [props.clientId]);

  useEffect(() => { void load(); }, [load]);

  /**
   * Until the secret lands this is the state of EVERY client, every day, so it
   * gets the CRM card's own proportion (F138 kolečko 3, fault 8): one quiet
   * sentence, not a framed block claiming importance. It carries no retry - a
   * repeat cannot configure a secret - and no warning mark, because nothing
   * failed and nothing about the client changed.
   */
  if (state === "unconfigured") {
    return (
      <p className="staff-license-quiet" data-purchase-license-state="unconfigured">
        {props.t("staffConsole.purchaseLicense.unconfigured")}
      </p>
    );
  }

  if (state === "error") {
    return (
      <p className="staff-license-quiet" role="status" data-purchase-license-state="error">
        <span className="staff-license-quiet-mark" aria-hidden="true">!</span>
        <span>
          {props.t("staffConsole.purchaseLicense.error")}{" "}
          <span className="staff-license-quiet-hint">{props.t("staffConsole.purchaseLicense.errorHint")}</span>
        </span>
        <button className="text-button" onClick={() => void load()} type="button">
          {props.t("common.retry")}
        </button>
      </p>
    );
  }

  return (
    <article
      className={`staff-card staff-license-card state-${state}`}
      data-purchase-license-state={state}
      aria-live="polite"
    >
      <h3>{props.t("staffConsole.purchaseLicense.title")}</h3>
      {state === "loading" && <p className="staff-license-status">{props.t("common.loading")}</p>}
      {state === "none" && <p className="staff-empty">{props.t("staffConsole.purchaseLicense.none")}</p>}
      {state === "ready" && <PurchaseLicenseList licenses={licenses} t={props.t} locale={props.locale} />}
    </article>
  );
}

/**
 * F155b: the rows, as a PURE component.
 *
 * Split out of the card deliberately: the card's own state arrives through a
 * `useEffect`, which never runs under `renderToStaticMarkup`, so a guard over
 * the card alone could only ever photograph the loading state. The list takes
 * its rows as a prop, so what a licence really renders is a test rather than a
 * screenshot somebody has to read.
 */
export function PurchaseLicenseList(props: {
  licenses: PurchaseLicenseRow[];
  t: (key: string) => string;
  locale: Locale;
}) {
  return (
    <ul className="staff-license-list">
      {props.licenses.map((row) => {
        /* 🔴 Eliška, 2026-08-17: "bylo by ale dobrý nějak zvýraznit ty, co mají
           productType start nebo empire." A client can hold several unactivated
           licences and the card shows every one of them (that decision did not
           change); what a partner is scanning for is the one that carries a
           package they SOLD. Anything else keeps rendering exactly as it did -
           the platform's own value, unhighlighted and unrenamed, because a word
           we have no name for is still a fact. */
        const archetype = productArchetypeOf(row.productType);
        return (
          <li
            key={row.license}
            className="staff-license-row"
            data-license-disabled={row.disabled ? "1" : "0"}
            /* Present ONLY for the two packages, so the rail, the badge and any
               later reader all branch on one attribute rather than each
               re-deciding what counts as a package. */
            {...(archetype ? { "data-license-package": archetype } : {})}
          >
            <div className="staff-license-head">
              <code className="staff-license-key">{row.license}</code>
              {archetype && (
                /* The badge is the package NAME, which is a product name and so
                   the same string in both markets (the F153 law) - never a
                   locale key, which would invite a translation of "Empire".
                   What IS per-language is the word saying what the chip is, and
                   the card already owns it as the label of the row below. */
                <span className="staff-license-badge">
                  <span className="visually-hidden">
                    {`${props.t("staffConsole.purchaseLicense.productType")}: `}
                  </span>
                  {PRODUCT_ARCHETYPE_LABELS[archetype]}
                </span>
              )}
            </div>
            <dl className="staff-definition">
              <div>
                <dt>{props.t("staffConsole.purchaseLicense.credits")}</dt>
                <dd>{row.credits === null
                  ? props.t("staffConsole.purchaseLicense.creditsUnknown")
                  : translateCount(props.locale, "staffConsole.purchaseLicense.creditsCount", row.credits)}</dd>
              </div>
              <div>
                <dt>{props.t("staffConsole.purchaseLicense.productType")}</dt>
                {/* A known package says its NAME here too, or one row would
                    print "Start" in the badge and "start" underneath it: the
                    same fact, said two ways, a hand's width apart. */}
                <dd>{archetype
                  ? PRODUCT_ARCHETYPE_LABELS[archetype]
                  : row.productType ?? props.t("staffConsole.purchaseLicense.productTypeUnknown")}</dd>
              </div>
              <div>
                <dt>{props.t("staffConsole.purchaseLicense.bot")}</dt>
                {/* An unbound licence is the one phase 4 of the Instance
                    Creator picks up, so "not on a bot yet" is a fact the
                    partner reads as readiness, never as a gap. */}
                <dd>{row.botUsername
                  ? `@${row.botUsername}`
                  : props.t("staffConsole.purchaseLicense.botNone")}</dd>
              </div>
              <div>
                <dt>{props.t("staffConsole.purchaseLicense.state")}</dt>
                <dd>{props.t(row.disabled
                  ? "staffConsole.purchaseLicense.stateDisabled"
                  : "staffConsole.purchaseLicense.stateActive")}</dd>
              </div>
            </dl>
          </li>
        );
      })}
    </ul>
  );
}
